import { type ErrorInfo, type ReactNode, Component as ReactComponent, Suspense, useEffect } from 'react'
import { type AppContext, type AppProps } from 'next/app'
import dynamic from 'next/dynamic'
import localFont from 'next/font/local'
import Head from 'next/head'
import Script from 'next/script'
import NextNprogress from 'nextjs-progressbar'
import { RenderRemoteComponents } from '@stone-payments/experiment/components'
import { ToastHub } from '@stone-payments/jade'
import { QueryClientProvider } from '@tanstack/react-query'
import dayjs from 'dayjs'
import Calendar from 'dayjs/plugin/calendar'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isBetween from 'dayjs/plugin/isBetween'
import LocalizedFormat from 'dayjs/plugin/localizedFormat'
import relativeTime from 'dayjs/plugin/relativeTime'
import Timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { webviewRuntime } from 'modules/shared/page-enhancers/src/utils/webview-runtime'
import { NuqsAdapter } from 'nuqs/adapters/next/pages'
import { analiticaInstance } from 'shared/analitica'
import { APP_ENV, APP_VERSION, appEnv, DEPLOY_TYPE, DEV_TOOLS, HOTJAR_ID, RECAPTCHA_SITE_KEY } from 'shared/envs'
import { Button, Link } from 'shared/experiment'
import { HttpClientProvider } from 'shared/http-client'
import { isomorphicCookies } from 'shared/storage'
import { ThemeProviders, useContaTheme } from 'shared/theming'
import { SwhConfigContextProvider } from 'swh/shared/components'

import { authenticator } from '~/domains/platform/core/authenticator/authenticator'
import { ToastProvider } from '~/domains/platform/layout/toast'
import { analytics } from '~/domains/platform/lib/analytics'
import { Bucket } from '~/domains/platform/lib/bucket'
import { clientSideOnly } from '~/domains/platform/lib/client-side-only'
import { useTrackPreviousRoute } from '~/domains/platform/lib/route-history/route-history'
import { initializeAnalytics } from '~/lib/helpers/utils/ports/analytics'
import { FullScreensProvider } from '~/ui/contexts/fullscreens'
import { queryClient } from '~/ui/hooks/utils/service/use-query-request'

const inter = localFont({
  src: [
    { path: '../public/fonts/inter/Inter-Regular.woff2', weight: '400', style: 'normal' },
    { path: '../public/fonts/inter/Inter-Medium.woff2', weight: '500', style: 'normal' },
    { path: '../public/fonts/inter/Inter-SemiBold.woff2', weight: '600', style: 'normal' },
    { path: '../public/fonts/inter/Inter-Bold.woff2', weight: '700', style: 'normal' }
  ],
  display: 'swap',
  preload: true
})

const sharonSans = localFont({
  src: [
    { path: '../public/fonts/SharonSans-Regular.woff2', weight: '400', style: 'normal' },
    { path: '../public/fonts/SharonSans-Medium.woff2', weight: '500', style: 'normal' },
    { path: '../public/fonts/SharonSans-Medium.woff2', weight: '600', style: 'normal' },
    { path: '../public/fonts/SharonSans-Bold.woff2', weight: '700', style: 'normal' }
  ],
  display: 'swap',
  preload: true,
  variable: '--font-sharon-sans'
})

const HomeTeamPendingKYCModal = dynamic(
  () => import('~/domains/kyc-home/components/home-team-pending-kyc-modal').then(m => m.HomeTeamPendingKYCModal),
  { ssr: false, loading: () => null }
)

import { hide } from 'shared/format-utils'

import { Loader } from '~/domains/platform/design-system/loader/loader'

import 'dayjs/locale/pt-br'

// eslint-disable-next-line @nx/enforce-module-boundaries
import packageInfo from '../package.json'

// eslint-disable-next-line import/no-unresolved -- generated by `pnpm build:css`, gitignored
import '~/ui/theme/globals.min.css'

dayjs.extend(LocalizedFormat)
dayjs.extend(customParseFormat)
dayjs.extend(Calendar)
dayjs.locale('pt-br')
dayjs.extend(utc)
dayjs.extend(Timezone)
dayjs.extend(isBetween)
dayjs.extend(relativeTime)

RenderRemoteComponents.registerJadeComponents()
RenderRemoteComponents.registerComponent('Link', Link)
RenderRemoteComponents.registerComponent('Button', Button)

let DevToolsPanel: any = null
if (DEV_TOOLS() === 'enabled') {
  // eslint-disable-next-line @nx/enforce-module-boundaries
  DevToolsPanel = dynamic<Record<any, never>>(() => import('../devtools').then(mod => mod.DevToolsPanel))
}

type CustomAppProps = AppProps & {
  isWebView?: boolean
}

export default function App({ Component, pageProps, isWebView: serverIsWebView }: CustomAppProps) {
  const currentTheme = useContaTheme()
  const { token } = isomorphicCookies.getAll()
  const subjectId = token ? authenticator.parseToken({ token })['stone_subject_id'] : undefined

  const cookies = isomorphicCookies.getAll()
  const isWebView = serverIsWebView ?? cookies.isWebView === 'true'
  clientSideOnly(() => hide())

  useTrackPreviousRoute()

  useEffect(() => {
    if (typeof window !== 'undefined' && webviewRuntime?.isWebView()) {
      isomorphicCookies.set('isWebView', 'true', { maxAge: 60 * 60 * 24 * 30 * 6, path: '/' })
    }
  }, [])

  useEffect(() => {
    analytics.update({ userId: subjectId })
    initializeAnalytics({ userId: subjectId, appVersion: packageInfo.version })
    logVersion()
    analiticaInstance.updateMetadata({ user: { id: subjectId } })

    if (process.env.NODE_ENV === 'development' && Bucket.local.get('exp::mock::enabled')) {
      // eslint-disable-next-line @nx/enforce-module-boundaries
      import('../devtools/plugins/index').then(mod => mod.enableExpHttpResponseMock())
    }
  }, [subjectId])

  useEffect(() => {
    ;(async () => {
      if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
        const { waitForMockFlags } = await import('shared/mocks')
        await waitForMockFlags()
      }
    })()
  }, [])

  const showFullExperience = !isWebView

  const content = (
    <Suspense fallback={<Loader />}>
      <NuqsAdapter>
        <QueryClientProvider client={queryClient}>
          <ThemeProviders>
            {showFullExperience && (
              <NextNprogress
                color={currentTheme.jade.theme.color.background['brand']}
                startPosition={0.3}
                stopDelayMs={200}
                height={4}
              />
            )}
            <HttpClientProvider>
              <ToastHub>
                <ToastProvider>
                  <FullScreensProvider>
                    {showFullExperience ? clientSideOnly(() => <HomeTeamPendingKYCModal />) : null}
                    <SwhConfigContextProvider {...pageProps}>
                      <Component {...pageProps} />
                    </SwhConfigContextProvider>
                    {showFullExperience && (APP_ENV() === 'sdb' || APP_ENV() === 'hml') && APP_VERSION() ? (
                      <a
                        href={`https://github.com/dlpco/dashboard/pull/${APP_VERSION()}`}
                        style={{
                          backgroundColor: 'gray',
                          borderRadius: '1rem',
                          bottom: '1rem',
                          color: 'white',
                          fontSize: '1rem',
                          fontWeight: 'bold',
                          padding: '0.5em 0.75em',
                          position: 'fixed',
                          left: '2rem',
                          opacity: 0.8,
                          textDecoration: 'none',
                          zIndex: 9999
                        }}
                      >
                        {DEPLOY_TYPE() === 'preview' ? `Preview do PR #${APP_VERSION()}` : `Versão ${APP_VERSION()}`}
                      </a>
                    ) : null}

                    {showFullExperience && DevToolsPanel ? clientSideOnly(() => <DevToolsPanel />) : null}
                  </FullScreensProvider>
                </ToastProvider>
              </ToastHub>
            </HttpClientProvider>
          </ThemeProviders>
        </QueryClientProvider>
      </NuqsAdapter>
    </Suspense>
  )

  return (
    <>
      <Head>
        {appEnv.isSandbox() ? <meta name="robots" content="noindex" /> : null}
        <meta charSet="utf-8" />
        <meta
          name="description"
          content="Acesse sua Conta da Stone.
          Confira suas vendas e recebimentos, crie links de pagamentos e realize transferências e Pix, tudo em um só lugar, na sua Conta Stone."
        />
        <meta httpEquiv="x-ua-compatible" content="ie=edge" />
        <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no" />
        <meta name="theme-color" content="#FFF" />
        <meta name="aplication-name" content="Stone" />
        {!isWebView && <meta name="hotjar-id" content={String(HOTJAR_ID())} />}
        <link rel="apple-touch-icon" href="/touchicon.png" />
        <link rel="icon" href="/favicon-light.png" type="image/png" media="(prefers-color-scheme: light)" />
        <link rel="icon" href="/favicon-dark.png" type="image/png" media="(prefers-color-scheme: dark)" />
        <link rel="preload" href="/favicon-light.png" as="image" type="image/png" fetchPriority="high" />
        <link rel="preload" href="/favicon-dark.png" as="image" type="image/png" />
        <link rel="preload" href="/touchicon.png" as="image" type="image/png" />

        <title>Stone</title>
      </Head>
      <div className={`${inter.className} ${sharonSans.variable}`}>
        {isWebView ? <WebviewGlobalErrorBoundary>{content}</WebviewGlobalErrorBoundary> : content}
      </div>
      {!isWebView && (
        <>
          <Script strategy="afterInteractive" src={previewURL('/scripts/hotjar.js')} />
          <Script strategy="beforeInteractive" src={previewURL('/scripts/core-sdk/FaceTecSDK.js/FaceTecSDK.js')} />
          <Script
            strategy="beforeInteractive"
            src={`https://www.google.com/recaptcha/enterprise.js?render=${RECAPTCHA_SITE_KEY()}`}
          />
          <Script
            dangerouslySetInnerHTML={{
              __html: `
                grecaptcha.enterprise.ready(function() {
                  globalThis.window.GRecaptcha = grecaptcha.enterprise
                });
              `
            }}
          />
        </>
      )}
    </>
  )
}

function previewURL(path: string) {
  return DEPLOY_TYPE() === 'preview' ? `/${APP_VERSION()}/${path}` : path
}

App.getInitialProps = async (appContext: AppContext) => {
  const { ctx } = appContext
  const cookies = isomorphicCookies.getAll(ctx)
  const cookieIsWebView = cookies.isWebView === 'true'

  const webviewQuery = ctx.query?.webview
  const queryIsWebView = webviewQuery === 'true' || (Array.isArray(webviewQuery) && webviewQuery[0] === 'true')

  const isWebView = cookieIsWebView || queryIsWebView

  return {
    isWebView: isWebView ?? false
  }
}

class WebviewGlobalErrorBoundary extends ReactComponent<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[WebviewGlobalErrorBoundary] Render error:', error, errorInfo)
    if (typeof window !== 'undefined' && webviewRuntime?.webviewErrorHandler) {
      webviewRuntime.webviewErrorHandler(error)
    }
  }

  render() {
    if (this.state.hasError) return null
    return this.props.children
  }
}

function logVersion() {
  console.info(`Build ${APP_VERSION()}`)
}
